import { TimeUtil } from 'rosbag';
import * as PPBTimeUtil from 'src/app/utils/time-utils';
import { ObjectUtils } from 'src/app/utils/object';
import {
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Type } from 'src/app/utils/type';
import { readROSBag } from './rosbag-reader';
import { RosbagBuffer } from './rosbag-buffer';
import { Injectable, OnDestroy } from '@angular/core';
import { NotificationService } from '../notification.service';
import { Time, ReadResult } from '../../models_new/types/rosbag/rosbag-types';

export type RosbagPlayerEventType =
  | '*'
  | 'play'
  | 'pause'
  | 'stop'
  // Signals when user starts the seeking action.
  | 'seekstart'
  | 'silderupdate'
  // Called before handling the seek event.
  | 'pre-sliderseek'
  // Called after handling the messages for the seek event.
  | 'post-sliderseek'
  // Signals when the user stops the seeking action.
  | 'seekstop'
  | 'playafterstop';
export interface RosbagPlayerEvent {
  type: RosbagPlayerEventType;
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class RosbagPlayerService implements OnDestroy {
  buffer: RosbagBuffer;

  ready$: Observable<boolean>;
  readySub: Subscription;

  destroy$ = new ReplaySubject<boolean>(1);

  readonly targetFPS = 30;

  playState: 'playing' | 'paused' | 'stopped' = 'stopped';
  private shouldRestartOnNextPlay = false;
  private isNextPlayAfterStop = false;
  private wasPlayingOnSeek = false;

  private seekingAvaiable = false;

  startTime: Time = { sec: 0, nsec: 0 } as Time;
  currentTime: Time = { sec: 0, nsec: 0 } as Time;
  prevTime: Time = { sec: 0, nsec: 0 } as Time;
  endTime: Time = { sec: 0, nsec: 0 } as Time;
  private keyframeIndex = 0;

  private currTimeMs: number;
  private deltaTimeMs = 0;
  private prevTimeMs: number;

  speed = 1;

  private _messages$ = new Subject<ReadResult>();
  messages$: Observable<ReadResult>;

  private _playerEvents$ = new Subject<RosbagPlayerEvent>();
  playerEvents$: Observable<RosbagPlayerEvent>;

  private intervalID: any;

  /**
   * Debug mode is used to simulate a 30fps animation cycle.
   * This is used to test the player independent of how much time has
   * actually passed between frames (as debug stepping is far far longer
   * than runtime frames).
   * **NOTE! It should be turned off in production**.
   */
  debugMode = false;

  constructor(private notification: NotificationService) {
    this.messages$ = this._messages$.asObservable();
    this.playerEvents$ = this._playerEvents$.asObservable();
  }

  ngOnDestroy(): void {
    if (this.readySub) {
      this.readySub.unsubscribe();
    }

    this.destroy$.next(true);
    this.destroy$.complete();
  }

  setDebugMode(mode: boolean): void {
    this.debugMode = mode;
  }

  load(url: string): void {
    const { buffer } = readROSBag(url);
    this.setBuffer(buffer);
  }

  private setBuffer(buffer: RosbagBuffer): void {
    this.buffer = buffer;

    this.ready$ = this.buffer.readProgress$.pipe(
      takeUntil(this.destroy$),
      map((v) => v === 100),
      tap((ready) => {
        if (ready) {
          this.initTimes();
        }
      })
    );

    this.buffer.error$
      .pipe(
        takeUntil(this.destroy$),
        // Make sure user knows if anything goes wrong
        tap((e: Error) => {
          console.error(e);
          this.notification.showError(
            `Failed to read recording. Try running a new simulation`
          );
          return of(false); // Things didn't go quite right, but dont panic.
        }),
        take(1)
      )
      .subscribe();
  }

  hasBuffer(): boolean {
    return typeof this.buffer !== 'undefined';
  }

  canSeek(): boolean {
    if (!this.hasBuffer()) {
      return false;
    }

    this.seekingAvaiable = this.buffer.readProgress$.getValue() === 100;

    return this.seekingAvaiable;
  }

  isPlaying(): boolean {
    return this.playState === 'playing';
  }

  isPaused(): boolean {
    return this.playState === 'paused';
  }

  isStopped(): boolean {
    return this.playState === 'stopped';
  }

  getPlaybackSpeed(): number {
    return this.speed;
  }

  getStartDisplayTime(): string {
    return PPBTimeUtil.toPrettyString({ sec: 0, nsec: 0 } as Time);
  }

  getEndDisplayTime(): string {
    if (!this.hasBuffer()) {
      return this.getStartDisplayTime();
    }

    return PPBTimeUtil.toPrettyString(this.buffer.getDurationTime(), false);
  }

  getCurrentDisplayTime(): string {
    return PPBTimeUtil.toPrettyString(
      PPBTimeUtil.sub(this.currentTime, this.startTime),
      false
    );
  }

  getKeyframeDisplayTime(index: number): string {
    const keyframe = this.buffer.getKeyframe(index);
    if (keyframe != null) {
      return PPBTimeUtil.toPrettyString(
        PPBTimeUtil.sub(keyframe.timestamp, this.startTime),
        false
      );
    } else {
      return this.getStartDisplayTime();
    }
  }

  getCurrentKeyframeIndex(): number {
    return this.keyframeIndex;
  }

  play(): void {
    if (!this.hasBuffer()) return;

    if (this.shouldRestartOnNextPlay) {
      this.stop();
      this.shouldRestartOnNextPlay = false;
    }

    if (this.isNextPlayAfterStop) {
      this.emitEvent('playafterstop');
      this.isNextPlayAfterStop = false;
    }

    // Used to calulate time since last animation update, set as same time to avoid 'jump' at animation start
    this.currTimeMs = new Date().getTime();
    this.prevTimeMs = new Date().getTime();
    if (!this.isPlaying()) {
      this.playState = 'playing';
      this.setupInvertalLoop();
    }
    this.emitEvent('play');
  }

  pause(): void {
    if (!this.hasBuffer()) return;

    this.playState = 'paused';
    this.emitEvent('pause');
  }

  stop(): void {
    if (!this.hasBuffer()) return;

    this.pause();
    this.restart();
    this.isNextPlayAfterStop = true;
    clearInterval(this.intervalID);
    this.emitEvent('stop');
  }

  seekToKeyframe(keyframeIndex: number): void {
    if (!this.hasBuffer()) return;

    const keyframe = this.buffer.getKeyframe(keyframeIndex);
    if (keyframe === null) {
      return;
    }
    // Find where we are in the messages buffer from the keyframe index.
    const kfMsgIndex = this.buffer.getFrameIndexFromKFIndex(keyframeIndex);

    // Get the last of all important / interesting messages.
    let msgs = this.buffer.lastOfMsgsInTopics(kfMsgIndex, {
      '/world/pallet/boxes': null,
      '/world/boxes': null,
      '/playback/joint_states': null,
      '/world/gripper': (msg: ReadResult) => {
        // Filters out messages that are not enabled and have no attached boxes,
        // which can cause boxes to be incorrectly placed on release if seeked to.
        return !(
          !msg.message.enabled && msg.message.attached_boxes.length === 0
        );
      },
    });
    // Search for collision messages until the previous keyframe if available.
    const lastIndex =
      keyframeIndex > 0
        ? this.buffer.getFrameIndexFromKFIndex(keyframeIndex - 1)
        : 0;
    const collMsgs = this.buffer.lastOfMsgsInTopics(
      kfMsgIndex,
      { '/robot_contact': null },
      true,
      lastIndex
    );

    if (this.shouldRestartOnNextPlay) {
      this.shouldRestartOnNextPlay = false;
    }

    this.currentTime = keyframe.timestamp;
    this.prevTime = keyframe.timestamp;
    this.keyframeIndex = keyframeIndex;

    // Handle seeking before updating and rendering
    this.emitEvent('pre-sliderseek');

    /*
    Need to be in a safe state in case we get interrupted for message processing.
    Will be un-paused if they seeked while playing elsewhere.
    */
    this.pause();

    // Handle all interesting messages between previous and current keyframe.
    for (const msg of msgs.concat(collMsgs, keyframe)) {
      this.handleMessage(msg);
    }

    // Update timestamps and timetracking data.
    // NOTE! If debugmode is on, simulate the time of 1 frame at 30fps (~33 ms).
    this.updateTimes(!this.debugMode ? 0 : 1000 / this.targetFPS);
    this.emitEvent('post-sliderseek');
  }

  setPlaybackSpeed(speed: number): void {
    if (speed < 0) {
      console.warn('Correcting illegal speed: ', speed, ', to 1');
      speed = 1;
    }
    this.speed = speed;
  }

  clear(): void {
    if (this.buffer) {
      this.buffer.destroy();
    }

    clearInterval(this.intervalID);

    this.playState = 'stopped';
    this.shouldRestartOnNextPlay = false;
    this.isNextPlayAfterStop = false;
    this.wasPlayingOnSeek = false;

    this.seekingAvaiable = false;

    this.startTime = { sec: 0, nsec: 0 } as Time;
    this.currentTime = { sec: 0, nsec: 0 } as Time;
    this.prevTime = { sec: 0, nsec: 0 } as Time;
    this.endTime = { sec: 0, nsec: 0 } as Time;
    this.keyframeIndex = 0;

    this.currTimeMs = undefined;
    this.deltaTimeMs = 0;
    this.prevTimeMs = undefined;

    this.speed = 1;
  }

  initTimes(): void {
    if (!this.hasBuffer()) return;

    // Fetch start time of bag, if not available, default to time of first message.
    let tempStartTime = this.buffer.info.startTime;
    if (typeof tempStartTime === 'undefined') {
      tempStartTime =
        typeof this.buffer.getFirstMessage() !== 'undefined'
          ? this.buffer.getFirstMessage().timestamp
          : { sec: 0, nsec: 0 };
    }

    if (!TimeUtil.areSame(this.startTime, tempStartTime)) {
      this.startTime = tempStartTime;
      this.restart(); // Update current time to be at start
    }

    let tempEndTime = this.buffer.info.endTime;
    if (typeof tempEndTime === 'undefined') {
      tempEndTime = { sec: 0, nsec: 0 };
    }
    if (typeof this.endTime === 'undefined') {
      this.endTime = { sec: 0, nsec: 0 };
    }

    if (!TimeUtil.areSame(this.endTime, tempEndTime)) {
      this.endTime = tempEndTime;
    }

    this.keyframeIndex = 0;
  }

  private handleMessage(
    msg: ReadResult,
    updateKeyframeIndex: boolean = true
  ): void {
    if (
      Object.keys(msg.message).includes('keyframe') &&
      msg.message['keyframe'] &&
      updateKeyframeIndex
    ) {
      // Find which keyframe this is, update where we are.
      this.keyframeIndex = this.buffer.findKFIndexFromMessageIndex(
        this.buffer.getMessageIndex(msg)
      );
    }

    this._messages$.next(msg);
  }

  private processMessagesBeetween(from: Time, to: Time): void {
    const msgs = this.buffer.getMessages(from, to);
    for (const msg of msgs) {
      this.handleMessage(msg);
    }
  }

  private emitEvent(event: RosbagPlayerEvent['type'], data?: any): void {
    this._playerEvents$.next({
      type: event,
      data: data,
    });
  }

  emitSeekStartEvent() {
    this.wasPlayingOnSeek = this.isPlaying();

    if (this.wasPlayingOnSeek) {
      this.pause();
    }
    this.emitEvent('seekstart');
  }

  emitSeekStopEvent() {
    this.emitEvent('seekstop');
    if (this.wasPlayingOnSeek && !this.isPlaying()) {
      this.play();
    }
  }

  private setupInvertalLoop(): void {
    this.intervalID = setInterval(
      this.animationCallback,
      1000 / this.targetFPS // Convert fps to cycle delay in ms.
    );
  }

  private updateTimes(deltaTimeMsOverride?: number): void {
    // Current timestamp becomes previous before addition of time since last update cycle
    this.prevTime = ObjectUtils.cloneObject(this.currentTime);

    // Update the current timestamp with delta time
    this.currentTime = PPBTimeUtil.addMillisecs(
      this.currentTime,
      (Type.isDefined(deltaTimeMsOverride)
        ? deltaTimeMsOverride
        : this.deltaTimeMs) * this.speed
    );

    if (
      TimeUtil.isGreaterThan(this.currentTime, this.buffer.info.endTime) ||
      TimeUtil.areSame(this.currentTime, this.buffer.info.endTime)
    ) {
      this.pause();
      this.shouldRestartOnNextPlay = true;
      this.currentTime = ObjectUtils.cloneObject(this.buffer.info.endTime);
    }
  }

  private updatePlayerComponent(): void {
    this.emitEvent('silderupdate', {
      keyframeIndex: this.keyframeIndex,
    });
  }

  private restart(): void {
    this.currentTime = ObjectUtils.cloneObject(this.startTime);
    this.keyframeIndex = 0;
  }

  private animationCallback = () => {
    if (this.isStopped()) {
      clearInterval(this.intervalID);
    }

    // Buffer doesnt have messages yet, skip this animationcycle and wait a little longer
    if (this.buffer.getNrBufferedMsgs() === 0) {
      return;
    }

    // Find time since last animation update cycle
    this.currTimeMs = new Date().getTime();
    this.deltaTimeMs = this.currTimeMs - this.prevTimeMs;
    this.prevTimeMs = this.currTimeMs;

    if (this.isPaused()) {
      // Paused, skip this cycle
      return;
    }

    // Update timestamps and timetracking data.
    // NOTE! If debugmode is on, simulate the time of 1 frame at 30fps (~33 ms).
    this.updateTimes(!this.debugMode ? undefined : 1000 / this.targetFPS);

    // Handle all messages between previous and current animationcycle
    this.processMessagesBeetween(this.prevTime, this.currentTime);

    this.updatePlayerComponent();
  };
}
